import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import createAttachments from 'owa-calendar-compose-form-store/lib/actions/createAttachments';
import getEventClientItemId from 'owa-calendar-compose-form-store/lib/utils/getEventClientItemId';
import { ApiError, ApiErrorCode, AttachmentDetails } from 'owa-addins-core';
import { AttachmentState, lazyDeleteAttachmentsViaQueueManager } from 'owa-attachment-well-data';
import type { AttachmentBlob } from '../utils/attachmentBlob';
import type { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import {
    createMailItemFiles,
    createUriFiles,
    createLocalComputerFiles,
} from 'owa-attachment-file-types';
import type { FullComposeViewState } from 'owa-calendar-compose-types';
import { onAttachmentCreated, onAttachmentDeleted } from 'owa-calendar-compose-form-actions';
import { saveItem } from './saveAdapter';
import { isImageFile } from 'owa-file';
import type { AttachmentContent } from 'owa-addins-common-utils/lib/getAttachmentContentHelper';
import { lazyGetAllAttachments } from 'owa-addins-common-utils';
import type AttachmentWellHost from 'owa-calendar-meeting-forms-common-store/lib/store/schema/AttachmentWellHost';
import { getAttachmentContentUtil } from '../utils/getAttachmentContentUtil';

function getAttachmentError(messageId: string): ApiErrorCode {
    switch (messageId) {
        case 'ErrorTooLargeForLocalAttachmentSingle':
        case 'ErrorTooLargeForLocalAttachmentMultiple':
            return ApiErrorCode.AttachmentSizeExceeded;
        default:
            return ApiErrorCode.AttachmentUploadGeneralFailure;
    }
}

function handleError(error: any): string {
    throw error.messageId
        ? getAttachmentError(error.messageId)
        : ApiErrorCode.AttachmentUploadGeneralFailure;
}

export const addItemAttachment = (viewState: FullComposeViewState) => (
    attachmentName: string,
    itemId: string,
    isInline: boolean
): Promise<string> => {
    return addItemOrFile(viewState, attachmentName, itemId, isInline, true).catch(handleError);
};

export const addFileAttachment = (viewState: FullComposeViewState) => (
    attachmentName: string,
    uri: string,
    isInline: boolean
): Promise<string> => {
    return addItemOrFile(viewState, attachmentName, uri, isInline, false).catch(handleError);
};

export const getAttachments = (viewState: FullComposeViewState) => (): Promise<
    AttachmentDetails[]
> => {
    return lazyGetAllAttachments.importAndExecute(viewState.attachmentWell);
};

export const addFileAttachmentFromBase64 = (viewState: FullComposeViewState) => (
    base64File: string,
    attachmentName: string,
    isInline: boolean
): Promise<string> => {
    return createAttachmentFromBase64String(viewState, base64File, attachmentName, isInline).catch(
        handleError
    );
};

export const removeAttachment = (viewState: FullComposeViewState) => (
    attachmentId: string
): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        lazyDeleteAttachmentsViaQueueManager.import().then(deleteAttachmentViaQueueManager => {
            try {
                let attachmentState: AttachmentState = getAttachmentState(
                    viewState.attachmentWell.docViewAttachments,
                    attachmentId
                );

                if (!attachmentState) {
                    attachmentState = getAttachmentState(
                        viewState.attachmentWell.imageViewAttachments,
                        attachmentId
                    );
                }

                if (!attachmentState) {
                    attachmentState = getAttachmentState(
                        viewState.attachmentWell.inlineAttachments,
                        attachmentId
                    );
                }

                if (!attachmentState) {
                    reject(new ApiError(ApiErrorCode.AttachmentToDeleteDoesNotExist));
                    return;
                }

                const onAttachmentDeletedCallback = (
                    parentItemId: ClientItemId,
                    attachmentId: ClientAttachmentId,
                    attachment: AttachmentType
                ) => {
                    onAttachmentDeleted(attachmentId, attachment);
                    resolve();
                };

                deleteAttachmentViaQueueManager(
                    viewState.attachmentWell,
                    attachmentState,
                    getEventClientItemId(),
                    null /* infoBar */,
                    onAttachmentDeletedCallback
                );
            } catch {
                reject(new ApiError(ApiErrorCode.AttachmentDeleteGeneralFailure));
            }
        });
    });
};

function addItemOrFile(
    viewState: FullComposeViewState,
    attachmentName: string,
    itemIdOrUrl: string,
    isInline: boolean,
    isItem: boolean
): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        let hasProcessed = false;

        const attachmentErrorHandler = () => {
            if (!hasProcessed) {
                hasProcessed = true;
                reject({ errorType: 'UploadError' });
            }
        };

        const onFinish = (attachmentId: ClientAttachmentId) => {
            if (!hasProcessed) {
                hasProcessed = true;
                resolve(attachmentId.Id);
            }
        };

        const onAttachmentCreatedCallback = (
            parentItemId: ClientItemId,
            attachmentId: ClientAttachmentId,
            attachment: AttachmentType
        ) => {
            onAttachmentCreated(attachmentId, attachment);
        };

        saveItem().then(() => {
            if (isItem) {
                const file = createMailItemFiles([attachmentName], [itemIdOrUrl]);
                createAttachments(
                    viewState,
                    getEventClientItemId(),
                    file,
                    { isInline, isHiddenInline: false, shouldShare: false },
                    null /* infoBar */,
                    onFinish,
                    onAttachmentCreatedCallback,
                    attachmentErrorHandler
                );
            } else {
                try {
                    const file = createUriFiles([{ name: attachmentName, url: itemIdOrUrl }]);

                    createAttachments(
                        viewState,
                        getEventClientItemId(),
                        file,
                        { isInline, isHiddenInline: true, shouldShare: false },
                        null /* infoBar */,
                        onFinish,
                        onAttachmentCreatedCallback,
                        attachmentErrorHandler
                    );
                } catch (err) {
                    reject({ errorType: 'UploadError' });
                }
            }
        });
    });
}

function createAttachmentFromBase64String(
    viewState: FullComposeViewState,
    base64String: string,
    attachmentName: string,
    isInline: boolean
): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        let hasProcessed = false;

        const attachmentErrorHandler = () => {
            if (!hasProcessed) {
                hasProcessed = true;
                reject({ errorType: 'UploadError' });
            }
        };

        const onFinish = (attachmentId: ClientAttachmentId) => {
            if (!hasProcessed) {
                hasProcessed = true;
                resolve(attachmentId.Id);
            }
        };

        const onAttachmentCreatedCallback = (
            parentItemId: ClientItemId,
            attachmentId: ClientAttachmentId,
            attachment: AttachmentType
        ) => {
            onAttachmentCreated(attachmentId, attachment);
        };

        saveItem().then(() => {
            try {
                const attachmentContent = atob(base64String);
                const attachmentContentLength = attachmentContent.length;
                const byteArray = new Uint8Array(new ArrayBuffer(attachmentContentLength));

                for (let i = 0; i < attachmentContentLength; i++) {
                    byteArray[i] = attachmentContent.charCodeAt(i);
                }

                var blob = null;
                if (isImageFile(attachmentName)) {
                    blob = new Blob([byteArray], { type: 'image/png' });
                } else {
                    blob = new Blob([byteArray]);
                }

                var attachmentBlob: AttachmentBlob = blob;
                attachmentBlob.name = attachmentName;

                const localComputerFileList = createLocalComputerFiles([<File>attachmentBlob]);
                createAttachments(
                    viewState,
                    getEventClientItemId(),
                    localComputerFileList,
                    { isInline, isHiddenInline: true, shouldShare: false },
                    null /* infoBar */,
                    onFinish,
                    onAttachmentCreatedCallback,
                    attachmentErrorHandler
                );
            } catch (err) {
                reject({ errorType: 'UploadError' });
            }
        });
    });
}

export function getAttachmentState(
    attachmentStates: AttachmentState[],
    attachmentId: string
): AttachmentState {
    const foundStates = attachmentStates.filter(state => state.attachmentId.Id === attachmentId);
    return foundStates.length > 0 ? foundStates[0] : null;
}

export const getAttachmentContent = (viewState: AttachmentWellHost) => (
    attachmentId: string
): Promise<AttachmentContent> => {
    return getAttachmentContentUtil(viewState.attachmentWell, attachmentId);
};
